// ============================================
// SettingsBridge.jsx - app-level bindings for the chat settings registry
// (utils/settingsRegistry) + the confirm card the runner awaits.
// Binds: theme (id + every THEME_COLOR_GROUPS colour) and trade defaults.
// Chart-scoped indicator settings are NOT bound here (chart bridge, later).
// Mount once inside TradeDefaultsProvider (AppContent).
// ============================================
import { useEffect, useRef, useState } from 'react'
import { useTradeDefaults } from './TradeDefaultsContext'
import { listThemes, getTheme, themeFieldValue, THEME_COLOR_GROUPS } from './themes'
import { defineSetting, bindSetting, registerMenu } from '../utils/settingsRegistry'
import { installGrokActions } from '../utils/grokActions'
import TourSpotlight from './TourSpotlight'

const sol = (lamports) => `${(Number(lamports) / 1e9).toFixed(4)} SOL`

// Trade defaults: confirm on every change; tight limits on size/leverage.
const TRADE_DEFS = [
  { key: 'buyAmountSol',        label: 'Market buy amount', min: 0.001, max: 5,    step: 0.01, format: v => `${v} SOL` },
  { key: 'sellPercentage',      label: 'Market sell amount', min: 1,    max: 100,  step: 1, integer: true, format: v => `${v}%` },
  { key: 'slippageBps',         label: 'Slippage',          min: 10,    max: 5000, step: 10, integer: true, format: v => `${Number(v) / 100}%` },
  { key: 'priorityFeeLamports', label: 'Priority fee',      min: 0,     max: 10_000_000, step: 1000, integer: true, format: sol },
  { key: 'jitoTipLamports',     label: 'Jito tip',          min: 0,     max: 10_000_000, step: 1000, integer: true, format: sol },
  { key: 'futuresQty',          label: 'Futures quantity',  min: 0.0001, max: 10,  step: 0.001 },
  { key: 'futuresLeverage',     label: 'Futures leverage',  min: 1,     max: 20,   step: 1, integer: true, format: v => `${v}x` },
]

export default function SettingsBridge({ themeId, setThemeId, themeOverrides, setThemeColor }) {
  const { defaults, setDefaults } = useTradeDefaults()
  // Latest values for getters without re-binding every render.
  const live = useRef({})
  live.current = { themeId, setThemeId, themeOverrides, setThemeColor, defaults, setDefaults }

  useEffect(() => {
    installGrokActions()
    const offs = []
    // ── Menus ──
    offs.push(registerMenu('theme', () => window.dispatchEvent(new CustomEvent('cheddar:open-theme', {
      detail: { x: Math.round(window.innerWidth / 2 - 120), y: 70 },
    }))))
    offs.push(registerMenu('tradeDefaults', () => window.dispatchEvent(new CustomEvent('cheddar:open-trade-defaults'))))

    // ── Theme ──
    const themes = listThemes()
    defineSetting({ id: 'theme.id', label: 'Theme', group: 'Theme', type: 'enum', scope: 'app', menu: 'theme',
      options: themes.map(t => ({ value: t.id, label: t.name })) })
    offs.push(bindSetting('theme.id', { get: () => live.current.themeId, set: (v) => live.current.setThemeId(v) }))
    for (const g of THEME_COLOR_GROUPS) {
      for (const f of g.fields) {
        const id = `theme.color.${f.path}`
        defineSetting({ id, label: `${g.label}: ${f.label}`, group: 'Theme colours', type: 'color', scope: 'app', menu: 'theme' })
        offs.push(bindSetting(id, {
          get: () => themeFieldValue(getTheme(live.current.themeId), live.current.themeOverrides, f.path),
          set: (v) => live.current.setThemeColor(f.path, v),
        }))
      }
    }

    // ── Trade defaults (confirm required) ──
    for (const d of TRADE_DEFS) {
      const id = `trade.${d.key}`
      defineSetting({ id, label: d.label, group: 'Trade defaults', type: 'number', scope: 'trade',
        min: d.min, max: d.max, step: d.step, integer: !!d.integer, format: d.format,
        menu: 'tradeDefaults', confirm: true })
      offs.push(bindSetting(id, {
        get: () => live.current.defaults?.[d.key],
        set: (v) => live.current.setDefaults({ [d.key]: v }),
      }))
    }
    return () => offs.forEach(off => { try { off() } catch {} })
  }, [])

  return <><SettingsConfirmCard /><TourSpotlight /></>
}

// Floating Confirm/Cancel card. Queue: one prompt at a time.
export function SettingsConfirmCard() {
  const [q, setQ] = useState([])
  useEffect(() => {
    const on = (e) => { const d = e?.detail; if (d && typeof d.resolve === 'function') setQ(p => [...p, d]) }
    window.addEventListener('cheddar:confirm', on)
    return () => window.removeEventListener('cheddar:confirm', on)
  }, [])
  const cur = q[0]
  useEffect(() => {
    if (!cur) return
    const k = (e) => { if (e.key === 'Escape') answer(false) }
    window.addEventListener('keydown', k)
    return () => window.removeEventListener('keydown', k)
  })
  if (!cur) return null
  function answer(v) { try { cur.resolve(v) } catch {} ; setQ(p => p.slice(1)) }
  return (
    <div className="settings-confirm-card" role="dialog" aria-label="Confirm setting change">
      <div className="scc-title">Grok wants to change {cur.title || 'a setting'}</div>
      <div className="scc-text">{cur.text}</div>
      <div className="scc-actions">
        <button type="button" className="scc-btn" onClick={() => answer(false)}>Cancel</button>
        <button type="button" className="scc-btn scc-ok" onClick={() => answer(true)}>Confirm</button>
      </div>
    </div>
  )
}
